import {
  LayoutDashboard,
  ListChecks,
  Briefcase,
  Building2,
  Users,
  BarChart3,
  Settings,
  Search,
  Send,
  DollarSign,
  Wrench,
  CheckSquare,
  Calendar,
  Sparkles,
  List,
  Database,
  Radio,
  Mail,
  Phone,
  MessageSquare,
  Workflow,
  Globe,
  FileInput,
  FileText,
  UserPlus,
} from "lucide-react";
import type { ComponentType, SVGProps } from "react";

export type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

export type NavLink = {
  href: string;
  label: string;
  icon: NavIcon;
  chip?: string;
};

export type NavSection = {
  id: string;
  label: string;
  icon: NavIcon;
  items: NavLink[];
};

export type NavConfig = {
  pins: NavLink[];
  sections: NavSection[];
  footer: NavLink[];
};

/** @deprecated Use NAV_CONFIG */
export type NavItem = NavLink;

/**
 * Apollo-aligned navigation.
 * Route mapping (internal surfaces → Apollo labels):
 *   Home → Home
 *   AI hub (narrative, AI, prompts, ICP) → AI Assistant
 *   Apollo people search → People (Directory)
 *   Job signal scan / AI hunt → Lists → Discover jobs
 *   Company directory → Companies (Directory)
 *   Intake / sources → Lists
 *   Signals market-pulse feed → Signals
 *   Data Health Center → Data enrichment
 *   Pipeline → Sequences
 *   Activity (email) → Emails
 *   Activity (call) → Calls
 *   Tasks → Tasks
 *   Meetings → Meetings
 *   Activity (all) → Conversations
 *   Opportunities → Deals
 *   Plugins / prompts hub → Workflows
 *   Analytics hub → Dashboard (Win deals)
 *   Visitors stub → Website visitors
 *   Proposals → Forms
 *   People (user) → People (Find & enrich)
 *   Companies (user) → Companies (Find & enrich)
 */
export const NAV_CONFIG: NavConfig = {
  pins: [
    { href: "/", label: "Home", icon: LayoutDashboard },
    { href: "/ai-assistant", label: "AI Assistant", icon: Sparkles },
  ],
  sections: [
    {
      id: "find-and-enrich",
      label: "Find & enrich",
      icon: Search,
      items: [
        { href: "/user/accounts", label: "Accounts", icon: Building2 },
        { href: "/user/leads", label: "Leads", icon: Users },
        { href: "/lists", label: "Lists", icon: List },
        { href: "/intake", label: "Intake", icon: Search },
        { href: "/enrichment", label: "Data enrichment", icon: Database },
        { href: "/signals", label: "Signals", icon: Radio },
      ],
    },
    {
      id: "engage",
      label: "Engage",
      icon: Send,
      items: [
        { href: "/sequences", label: "Sequences", icon: ListChecks },
        { href: "/emails", label: "Emails", icon: Mail },
        { href: "/calls", label: "Calls", icon: Phone },
        { href: "/tasks", label: "Tasks", icon: CheckSquare },
      ],
    },
    {
      id: "win-deals",
      label: "Win deals",
      icon: DollarSign,
      items: [
        { href: "/meetings", label: "Meetings", icon: Calendar },
        { href: "/conversations", label: "Conversations", icon: MessageSquare },
        { href: "/deals", label: "Deals", icon: Briefcase },
        { href: "/dashboard", label: "Dashboard", icon: BarChart3 },
        { href: "/proposals", label: "Proposals", icon: FileText },
      ],
    },
    {
      id: "tools-and-automation",
      label: "Tools and automation",
      icon: Wrench,
      items: [
        { href: "/workflows", label: "Workflows", icon: Workflow },
      ],
    },
    {
      id: "inbound",
      label: "Inbound",
      icon: Globe,
      items: [
        { href: "/visitors", label: "Website visitors", icon: Globe },
        { href: "/forms", label: "Forms", icon: FileInput },
      ],
    },
    {
      id: "directory",
      label: "Directory",
      icon: Globe,
      items: [
        { href: "/companies", label: "Companies", icon: Building2 },
        { href: "/people", label: "People", icon: Users },
      ],
    },
  ],
  footer: [
    { href: "/settings/users", label: "Add teammates", icon: UserPlus },
    { href: "/settings/account", label: "Admin Settings", icon: Settings },
  ],
};

export const NAV_ITEMS: NavLink[] = [
  ...NAV_CONFIG.pins,
  ...NAV_CONFIG.sections.flatMap((s) => s.items),
  ...NAV_CONFIG.footer,
];

const EXPANDED_KEY = "sales-ops.nav.expanded";

function parseHref(href: string): { pathname: string; search: string } {
  const q = href.indexOf("?");
  if (q === -1) return { pathname: href, search: "" };
  return { pathname: href.slice(0, q), search: href.slice(q) };
}

/** Path prefixes that should treat route aliases as active. */
const PATH_ALIASES: Record<string, string[]> = {
  "/people": ["/people"],
  "/lists": ["/lists"],
  "/intake": ["/intake"],
  "/enrichment": ["/enrichment"],
  "/signals": ["/signals"],
  "/sequences": ["/sequences"],
  "/emails": ["/emails"],
  "/calls": ["/calls"],
  "/conversations": ["/conversations", "/activity"],
  "/deals": ["/deals", "/opportunities", "/pipeline"],
  "/workflows": ["/workflows"],
  "/forms": ["/forms"],
  "/proposals": ["/proposals"],
  "/visitors": ["/visitors", "/websites"],
  "/ai-assistant": ["/ai-assistant"],
  "/user/leads": ["/user/leads"],
  "/user/accounts": ["/user/accounts"],
  "/meetings": ["/meetings"],
  "/tasks": ["/tasks"],
  "/dashboard": ["/dashboard", "/analytics"],
  "/analytics": ["/analytics", "/dashboard"],
  "/": ["/"],
  "/settings/account": ["/settings"],
  "/settings/workspace": ["/settings/workspace"],
};

export function isActivePath(
  href: string,
  pathname: string,
  search = "",
): boolean {
  const { pathname: targetPath, search: targetSearch } = parseHref(href);

  if (targetPath === "/") return pathname === "/";

  if (targetPath === "/settings/account") {
    return pathname.startsWith("/settings");
  }

  if (targetPath === "/settings/workspace") {
    return (
      pathname.startsWith("/settings/workspace") ||
      pathname.startsWith("/settings/users")
    );
  }

  if (targetPath === "/companies") {
    return pathname === "/companies" || pathname.startsWith("/companies/");
  }

  const aliases = PATH_ALIASES[targetPath] ?? [targetPath];
  return aliases.some((alias) => {
    if (alias === "/") return pathname === "/";
    return pathname === alias || pathname.startsWith(`${alias}/`);
  });
}

export function sectionContainsPath(
  section: NavSection,
  pathname: string,
  search = "",
): boolean {
  return section.items.some((item) => isActivePath(item.href, pathname, search));
}

export function sectionIdForPath(pathname: string, search = ""): string | null {
  for (const section of NAV_CONFIG.sections) {
    if (sectionContainsPath(section, pathname, search)) return section.id;
  }
  return null;
}

export function loadExpandedSections(pathname: string, search = ""): Set<string> {
  const active = sectionIdForPath(pathname, search);
  let stored: string[] = [];
  try {
    const raw = localStorage.getItem(EXPANDED_KEY);
    if (raw) stored = JSON.parse(raw) as string[];
  } catch {
    /* ignore */
  }
  const next = new Set(
    stored.length > 0 ? stored : NAV_CONFIG.sections.map((s) => s.id),
  );
  if (active) next.add(active);
  return next;
}

export function persistExpandedSections(ids: Set<string>): void {
  try {
    localStorage.setItem(EXPANDED_KEY, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

const COLLAPSED_KEY = "sales-ops.nav.collapsed";

export function loadNavCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function persistNavCollapsed(collapsed: boolean): void {
  try {
    localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
  } catch {
    /* ignore */
  }
}
